import { TransportIcon } from '@app/ccf/components/transport-icon';
import { nodeTypeOptions } from '@app/ccf/options/node-options';
import { storageDurationOptions } from '@app/ccf/options/storage-duration-options';
import { useGetTransportModes } from '@shared/api/hooks/ccf/transport-modes';
import { TypeOfSuppliersEnum } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import GooglePlacePicker from '@shared/components/form/google-place-picker';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import { RepeatableRowComponentProps } from '@shared/components/form/types';
import { Button } from '@shared/components/ui/button';
import { Skeleton } from '@shared/components/ui/skeleton';
import { cn } from '@shared/lib/utils';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormattedTransportModes } from '../../utils';
import {
  supplyInterchangeSiteOptions,
  supplyOriginSitesOptions,
} from '../supply-options';

interface SupplyFlowRowProps extends RepeatableRowComponentProps {}

const getRowIcon = (nodeType: string) => {
  const icon = nodeTypeOptions.find(
    (option) => option.value === nodeType
  )?.icon;

  return icon || <Icons.arrowUpDown className="size-6 text-primary-400" />;
};

export default function FlowReviewRow({
  index,
  remove,
  insert,
}: SupplyFlowRowProps) {
  const { watch } = useFormContext();

  const { length } = watch('segments');

  const typeOfSuppliers = watch('typeOfSuppliers');

  const internationalSupplyFlow = useMemo(
    () => typeOfSuppliers === TypeOfSuppliersEnum['international_supplier'],
    [typeOfSuppliers]
  );

  const { data: transportModes, isLoading: transportModesLoading } =
    useGetTransportModes({
      params: {},
    });

  const transportModeOptions = useFormattedTransportModes(
    transportModes?.results || []
  );

  const isFirstRow = index === 0;

  const thisRowNodeType = watch(`segments.${index}.nodeType`);
  const thisRowTransportMode = watch(`segments.${index}.transportMode`);

  const supplyNodeTypeOptions = useMemo(() => {
    if (isFirstRow) {
      return supplyOriginSitesOptions;
    }

    if (!internationalSupplyFlow) {
      return supplyInterchangeSiteOptions.filter(
        (option) => option.value !== 'port'
      );
    }

    return supplyInterchangeSiteOptions;
  }, [internationalSupplyFlow, isFirstRow]);

  if (transportModesLoading) {
    return <Skeleton className="h-12 w-full rounded-md bg-gray-100" />;
  }

  return (
    <div className="flex space-y-2">
      <div className="max-w-3xl space-y-2">
        <div
          className={cn(
            'flex w-[700px] h-[60px] items-center space-x-3 rounded-md border-2 border-primary-100 bg-gray-00 px-4 py-2',
            {
              'border-primary-400': isFirstRow,
            }
          )}
        >
          <div className={cn('text-primary-400')}>
            {isFirstRow ? (
              <Icons.package className="size-6" />
            ) : (
              getRowIcon(thisRowNodeType)
            )}
          </div>
          <div className="flex w-full items-center space-x-2">
            {isFirstRow ? (
              <div className="flex h-8 items-center">
                <Text className="text-gray-500">
                  Your supplier&apos;s sites
                </Text>
              </div>
            ) : (
              <>
                <SelectFormField
                  name={`segments.${index}.nodeType`}
                  options={supplyNodeTypeOptions}
                  placeholder="Site"
                  className="w-40"
                />
                <SelectFormField
                  name={`segments.${index}.storageDuration`}
                  options={storageDurationOptions}
                  placeholder="Storage"
                  className="w-40"
                />
              </>
            )}

            {internationalSupplyFlow && (
              <div className="flex flex-1 justify-end">
                <GooglePlacePicker
                  name={`segments.${index}.nodeName`}
                  className="w-60"
                  placeholder="Location"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex w-full items-center space-x-2 px-4 pl-3">
          <div className="mr-1 flex items-center space-x-2">
            <Icons.arrowDown className="size-8 text-primary-400" />
            <TransportIcon
              transportModeId={thisRowTransportMode}
              transportModeOptions={transportModeOptions}
            />
          </div>
          <SelectFormField
            name={`segments.${index}.transportMode`}
            options={transportModeOptions}
            className="w-32 shrink-0"
            placeholder="Transport"
          />
          <InputFormField
            name={`segments.${index}.distance`}
            className="w-40 shrink-0"
            inputClassName="text-right"
            placeholder="Distance"
            adornmentRight="km"
            type="number"
            min={1}
          />
        </div>
      </div>
      {(!isFirstRow || length < 2) && (
        <div className="flex text-primary-400">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('size-12 -mt-0.5')}
            onClick={() => {
              insert(index + 1, {
                nodeType: '',
                transportMode: '',
                distance: '',
                storageDuration: '',
                ...(internationalSupplyFlow && {
                  nodeName: '',
                }),
              });
            }}
          >
            <Icons.plus className="size-6" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('size-12 -mt-0.5')}
            disabled={length === 1}
            onClick={() => {
              remove(index);
            }}
          >
            <Icons.x className="size-6" />
          </Button>
        </div>
      )}
    </div>
  );
}
